import { FieldError, StepValidationResult, SummaryGroup } from './types';

/** Read a nested value by dot-path. Returns undefined if any segment is missing. */
export function getByPath(obj: unknown, path: string): unknown {
  if (obj == null) return undefined;
  const parts = path.split('.');
  let current: any = obj;
  for (const part of parts) {
    if (current == null) return undefined;
    current = current[part];
  }
  return current;
}

/**
 * True if a value is "missing" for validation purposes:
 * null, undefined, NaN, empty string, or whitespace-only string.
 *
 * Note: `0`, `'0'`, and `false` are NOT empty — they represent real
 * user-provided values. Arrays and objects are also NOT empty; validators
 * that need to flag empty collections should check `.length === 0` or
 * `Object.keys().length === 0` explicitly.
 */
export function isEmpty(value: unknown): boolean {
  if (value == null) return true;
  if (typeof value === 'number' && Number.isNaN(value)) return true;
  if (typeof value === 'string') return value.trim() === '';
  return false;
}

/**
 * User-facing bullet for one field error. Uses summaryMessage when set, else
 * "{label} is required" / "is recommended". Ensures trailing punctuation so each
 * reads as a complete sentence.
 */
export function fieldErrorLine(err: FieldError): string {
  const text = (err.summaryMessage ?? `${err.label} ${err.severity === 'warn' ? 'is recommended' : 'is required'}`).trim();
  return /[.!?]$/.test(text) ? text : `${text}.`;
}

/** Group field errors by entityId, preserving order of first appearance. */
export function groupErrorsBySummary(errors: FieldError[]): SummaryGroup[] {
  const byEntity = new Map<string, SummaryGroup>();
  for (const err of errors) {
    let group = byEntity.get(err.entityId);
    if (!group) {
      group = { entityLabel: err.entityLabel, fields: [] };
      byEntity.set(err.entityId, group);
    }
    group.fields.push(fieldErrorLine(err));
  }
  return Array.from(byEntity.values());
}

/**
 * Summary object for a step's validation banner — pre-computed so templates
 * can do direct property lookups instead of calling methods per CD cycle.
 */
export interface ValidationSummary {
  errorGroups: SummaryGroup[];
  warningGroups: SummaryGroup[];
  totalErrors: number;
  totalWarnings: number;
}

/**
 * Result of turning a StepValidationResult into the template lookup maps
 * all four step components need. Keeps the transform logic in one place
 * so every consumer builds the maps identically.
 */
export interface FieldErrorMaps {
  /** byField[entityId][fieldPath] = true when that field has an error */
  byField: Record<string, Record<string, boolean>>;
  /** byEntity[entityId] = true when the entity has any error */
  byEntity: Record<string, boolean>;
  /** byFieldWarn[entityId][fieldPath] = true when that field has a warning */
  byFieldWarn: Record<string, Record<string, boolean>>;
  /** Pre-computed summary for the validation banner */
  summary: ValidationSummary;
}

/**
 * Transform a StepValidationResult into the pre-computed lookup maps that
 * step component templates use for red-border / warning-border bindings and
 * the summary banner. Replaces the near-identical rebuildFieldErrors logic
 * that was duplicated across LEN/SA/Meter/Bill-Review step components.
 */
export function buildFieldErrorMaps(result: StepValidationResult): FieldErrorMaps {
  const byField: Record<string, Record<string, boolean>> = {};
  const byEntity: Record<string, boolean> = {};
  for (const err of result.errors) {
    (byField[err.entityId] ??= {})[err.field] = true;
    byEntity[err.entityId] = true;
  }

  const byFieldWarn: Record<string, Record<string, boolean>> = {};
  for (const warn of result.warnings) {
    (byFieldWarn[warn.entityId] ??= {})[warn.field] = true;
  }

  return {
    byField,
    byEntity,
    byFieldWarn,
    summary: {
      errorGroups: groupErrorsBySummary(result.errors),
      warningGroups: groupErrorsBySummary(result.warnings),
      totalErrors: result.errors.length,
      totalWarnings: result.warnings.length,
    },
  };
}
